import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Mail, Lock, User, ArrowLeft, Loader2, Home, Briefcase } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userType, setUserType] = useState(searchParams.get("type") || "customer");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "business" || type === "customer") {
      setUserType(type);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    // Validate all fields
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedName || !trimmedEmail || !password || !confirmPassword) {
      toast.error(t("auth.fillAllFields"));
      return;
    }

    if (trimmedName.length < 2) {
      toast.error(t("auth.nameTooShort"));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      toast.error(t("auth.invalidEmail"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }

    setLoading(true);
    try {
      const user = await register(trimmedEmail, password, trimmedName, userType);
      toast.success(`Welcome to MyProjectBidder, ${user.name}!`);
      
      // Redirect based on user type
      if (user.user_type === "customer") navigate("/customer");
      else if (user.user_type === "business") navigate("/business/profile");
    } catch (error) {
      // Handle different error types
      if (error.code === "ERR_NETWORK" || !error.response) {
        toast.error(t("auth.networkErrorReg"));
      } else if (error.response?.status === 400) {
        const message = error.response?.data?.detail || t("auth.emailRegistered");
        toast.error(message);
      } else if (error.response?.status === 422) {
        toast.error(t("auth.invalidInput"));
      } else if (error.response?.status >= 500) {
        toast.error(t("auth.serverError"));
      } else {
        const message = error.response?.data?.detail || t("auth.registrationFailed");
        toast.error(message);
      }
      console.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0B1F3B] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.backToHome")}
          </Link>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <span className="text-xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
                {t("common.appName")}
              </span>
            </div>
            <LanguageSwitcher />
          </div>

          <h1 className="text-3xl font-extrabold text-[#0B1F3B] mb-2" style={{ fontFamily: 'Manrope' }}>
            {t("auth.createAccount")}
          </h1>
          <p className="text-slate-600 mb-8">
            {t("auth.createAccountSubtitle")}
          </p>

          {/* User Type Toggle */}
          <div className="flex gap-4 mb-8">
            <button
              type="button"
              onClick={() => setUserType("customer")}
              className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                userType === "customer"
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="customer-type-btn"
            >
              <Home className={`w-6 h-6 mx-auto mb-2 ${userType === "customer" ? "text-[#D4AF37]" : "text-slate-400"}`} />
              <p className={`font-semibold ${userType === "customer" ? "text-[#0B1F3B]" : "text-slate-600"}`}>
                {t("auth.homeowner")}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t("auth.getEstimates")}</p>
            </button>
            <button
              type="button"
              onClick={() => setUserType("business")}
              className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                userType === "business"
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="business-type-btn"
            >
              <Briefcase className={`w-6 h-6 mx-auto mb-2 ${userType === "business" ? "text-[#D4AF37]" : "text-slate-400"}`} />
              <p className={`font-semibold ${userType === "business" ? "text-[#0B1F3B]" : "text-slate-600"}`}>
                {t("auth.contractor")}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t("auth.submitBids")}</p>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#0B1F3B] font-medium">
                {t("auth.fullName")}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0B1F3B] font-medium">
                {t("auth.emailAddress")}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#0B1F3B] font-medium">
                {t("auth.password")}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#0B1F3B] font-medium">
                {t("auth.confirmPassword")}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("auth.creatingAccount")}
                </>
              ) : (
                t("auth.createAccountBtn")
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-sm text-slate-400">{t("common.or")}</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>

          {/* Google Register */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-slate-300 font-medium"
            onClick={() => {
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t("auth.continueWithGoogle")}
          </Button>

          <p className="text-center text-slate-600 mt-6">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C048] font-semibold">
              {t("auth.signInLink")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:flex flex-1 bg-[#0B1F3B] items-center justify-center p-12">
        <div className="max-w-lg text-center">
          <img
            src="https://images.unsplash.com/photo-1762811054950-b74e0a055c80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc3MDAwMTgzOXww&ixlib=rb-4.1.0&q=85"
            alt="Luxury home"
            className="rounded-2xl shadow-2xl mb-8"
          />
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            {userType === "business" ? t("auth.growBusiness") : t("auth.findPros")}
          </h2>
          <p className="text-slate-300">
            {userType === "business"
              ? t("auth.growSubtitle")
              : t("auth.findSubtitle")}
          </p>
        </div>
      </div>
    </div>
  );
}
